// src/utils/Onboarding/smsApi.js

/**
 * SMS API Client
 * 
 * Firebase function clients for triggering SMS notifications.
 * Used when users save their report via phone number submission.
 * 
 * IMPORTANT: These functions do NOT throw - they return result objects
 * with success/error info for tracking and Firestore sync.
 */

const SMS_BASE_URL = 'https://europe-west3-match-medic-p0.cloudfunctions.net';

/**
 * Send SMS with analysis report link.
 * Called when user submits phone on ImageAnalysisSlide (step 5.5).
 * 
 * @param {Object} params
 * @param {string} params.targetNumber - Phone in E.164 format (e.g., +447123456789)
 * @param {string} params.requestId - Analysis requestId from API response
 * @param {string} params.password - Password from API response (firestore.password)
 * @param {string} [params.userName] - Optional user name for personalization
 * @returns {Promise<Object>} Result object with success/error info
 * 
 * @example
 * const result = await sendAnalysisSMS({
 *   targetNumber: '+447123456789',
 *   requestId: 'req_abc123',
 *   password: 'xY7kL9mN',
 *   userName: 'John'
 * });
 * // result.success === true/false
 */
export async function sendAnalysisSMS({ targetNumber, requestId, password, userName }) {
  const startTime = Date.now();
  
  console.log('[SMS System] sendAnalysisSMS called with:', {
    targetNumber: targetNumber ? `${targetNumber.slice(0, 6)}...` : null,
    requestId,
    hasPassword: !!password,
    userName,
  });
  
  // Validate required params
  if (!targetNumber || !requestId || !password) {
    console.error('[SMS System] Missing required parameters:', {
      hasTargetNumber: !!targetNumber,
      hasRequestId: !!requestId,
      hasPassword: !!password,
    });
    return {
      success: false,
      error: 'Missing required parameters',
      errorDetails: {
        hasTargetNumber: !!targetNumber,
        hasRequestId: !!requestId,
        hasPassword: !!password,
      },
      duration: `${Date.now() - startTime}ms`,
      timestamp: new Date().toISOString(),
    };
  }
  
  const url = `${SMS_BASE_URL}/sendSMS_ImageAnalysis_1`;
  
  // Build request body
  const body = {
    targetNumber,
    requestId,
    password,
    ...(userName && { userName }),
  };
  
  console.log('[SMS System] Sending analysis SMS request to:', url);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    
    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
    
    const duration = `${Date.now() - startTime}ms`;
    
    if (!response.ok || data.success === false) {
      console.error('[SMS System] Analysis SMS failed:', {
        httpStatus: response.status,
        error: data.error || data.message,
        data,
      });
      return {
        success: false,
        error: data.error || data.message || `HTTP ${response.status}`,
        errorCode: data.errorCode || null,
        httpStatus: response.status,
        duration,
        timestamp: new Date().toISOString(),
        requestId,
        targetNumber,
      };
    }
    
    console.log('[SMS System] Analysis SMS sent successfully:', {
      messageSid: data.messageSid,
      status: data.status,
      duration,
    });
    return {
      success: true,
      messageSid: data.messageSid || null,
      status: data.status || null,
      messagePreview: data.messagePreview || null,
      numSegments: data.numSegments || null,
      duration,
      timestamp: new Date().toISOString(),
      requestId,
      targetNumber,
    };
    
  } catch (error) {
    console.error('[SMS System] Analysis SMS network error:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      errorType: 'network',
      duration: `${Date.now() - startTime}ms`,
      timestamp: new Date().toISOString(),
      requestId,
      targetNumber,
    };
  }
}

/**
 * Send SMS with generation (before/after) report link.
 * Called when user submits phone on BeforeAfterSlide (step 11.5).
 * 
 * @param {Object} params
 * @param {string} params.targetNumber - Phone in E.164 format (e.g., +447123456789)
 * @param {string} params.requestId - Generation requestId from API response
 * @param {string} params.password - Password from API response (firestore.password)
 * @param {string} [params.userName] - Optional user name for personalization
 * @returns {Promise<Object>} Result object with success/error info
 * 
 * @example
 * const result = await sendGenerationSMS({
 *   targetNumber: '+447123456789',
 *   requestId: 'gen_xyz789',
 *   password: 'aB3cD4eF',
 *   userName: 'John'
 * });
 */
export async function sendGenerationSMS({ targetNumber, requestId, password, userName }) {
  const startTime = Date.now();
  
  console.log('[SMS System] sendGenerationSMS called with:', {
    targetNumber: targetNumber ? `${targetNumber.slice(0, 6)}...` : null,
    requestId,
    hasPassword: !!password,
    userName,
  });
  
  // Validate required params
  if (!targetNumber || !requestId || !password) {
    console.error('[SMS System] Missing required parameters:', {
      hasTargetNumber: !!targetNumber,
      hasRequestId: !!requestId,
      hasPassword: !!password,
    });
    return {
      success: false,
      error: 'Missing required parameters',
      errorDetails: {
        hasTargetNumber: !!targetNumber,
        hasRequestId: !!requestId,
        hasPassword: !!password,
      },
      duration: `${Date.now() - startTime}ms`,
      timestamp: new Date().toISOString(),
    };
  }
  
  const url = `${SMS_BASE_URL}/sendSMS_ImageGeneration_1`;
  
  // Build request body
  const body = {
    targetNumber,
    requestId,
    password,
    ...(userName && { userName }),
  };
  
  console.log('[SMS System] Sending generation SMS request to:', url);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    
    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
    
    const duration = `${Date.now() - startTime}ms`;
    
    if (!response.ok || data.success === false) {
      console.error('[SMS System] Generation SMS failed:', {
        httpStatus: response.status,
        error: data.error || data.message,
        data,
      });
      return {
        success: false,
        error: data.error || data.message || `HTTP ${response.status}`,
        errorCode: data.errorCode || null,
        httpStatus: response.status,
        duration,
        timestamp: new Date().toISOString(),
        requestId,
        targetNumber,
      };
    }
    
    console.log('[SMS System] Generation SMS sent successfully:', {
      messageSid: data.messageSid,
      status: data.status,
      duration,
    });
    return {
      success: true,
      messageSid: data.messageSid || null,
      status: data.status || null,
      messagePreview: data.messagePreview || null,
      numSegments: data.numSegments || null,
      duration,
      timestamp: new Date().toISOString(),
      requestId,
      targetNumber,
    };
    
  } catch (error) {
    console.error('[SMS System] Generation SMS network error:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      errorType: 'network',
      duration: `${Date.now() - startTime}ms`,
      timestamp: new Date().toISOString(),
      requestId,
      targetNumber,
    };
  }
}
